<template>
    <div class="quiz" v-if="quiz && quiz.questions && quiz.questions.length">
        <div class="quiz__content" v-if="this.questionNumber < this.quiz.questionsInQuiz && !showQuizTimeOut && !quizStatus.isCompleted">
            <div class="quiz__progress">
                <span v-html="$t('Quiz.question_number_of_questions', { questionNumber: questionNumber + 1, questionsLength: quiz.questions.length })">{{ questionNumber + 1 }} of {{ quiz.questions.length }} questions</span>
                <QuizProgress :progress="progress" />
            </div>
            <span class="quiz__question" v-html="quiz.questions[questionNumber].question"></span>
            <div class="quiz__attachments" v-if="quiz.questions[questionNumber].attachments && quiz.questions[questionNumber].attachments.length">
                <span class="quiz__attachment" v-for="(attachment, index) in quiz.questions[questionNumber].attachments" :key="index">
                    <img v-if="attachment.type === 'image'" :src="attachment.url" alt="attachment image" />
                    <iframe
                        v-else-if="attachment.type === 'video' && isVimeoUrl(attachment.url)"
                        :src="attachment.url"
                        frameborder="0"
                        allow="autoplay; fullscreen"
                        allowfullscreen
                        width="640"
                        height="360">
                    </iframe>
                </span>
            </div>
            <div class="quiz__answers">
                <span class="quiz__hint" v-html="$t('Quiz.select_only_one_answer')"></span>
                <QuizChoice
                    v-for="(choice, index) in quiz.questions[questionNumber].choices"
                    :key="index"
                    :answer="choice"
                    :label="alphabet[index]"
                    :isSelected="selectedAnswerIndex === index"
                    :isSelectedAnswerCorrect="selectedAnswerIndex === index && isCorrectAnswer"
                    :isSelectedAnswerIncorrect="selectedAnswerIndex === index && !isCorrectAnswer && isCorrectAnswer !== null"
                    :correctChoice="correctChoiceIndex === index"
                    :isAnswerSubmitted="answerSubmitted"
                    @select-answer="updateSelectedChoice(index)"
                    v-if="quizType === 'choices'"
                />
                <QuizTextarea @textarea-validation-update="updateTextareaValidation" v-model="textareaAnswer" v-if="quizType === 'text'"/>
            </div>
            <div :class="['quiz__feedback', { 'correct': isCorrectAnswer }, { 'incorrect': !isCorrectAnswer }]" v-if="isCorrectAnswer !== null">
                <span :class="feedback.icon"></span>
                <span v-html="feedback.text"></span>
            </div>
            <TotemButton color="primary" :label=button.label :icon="button.icon" iconPosition="right" @click="submitAnswer" class="quiz__submit-button" :disabled="disabledButton"/>
        </div>
        <QuizResult
            @retake-test="resetQuiz"
            :results="results"
            :isQuizPassed="isQuizPassed"
            :quizStatus="quizStatus"
            :quizLength="quiz.questions.length"
            v-if="(this.questionNumber === quiz.questions.length) || quizStatus.isCompleted"
         />
        <QuizTimeOut v-if="showQuizTimeOut" @retake-test="resetQuiz" />
    </div>
</template>

<script>
    import QuizChoice from 'components/coursepage/quiz/QuizChoice'
    import QuizProgress from 'components/coursepage/quiz/QuizProgress'
    import QuizTextarea from 'components/coursepage/quiz/QuizTextarea'
    import QuizResult from 'components/coursepage/quiz/QuizResult'
    import QuizTimeOut from 'components/coursepage/quiz/QuizTimeOut'
    
    export default {
        name: 'quiz',
        components: { QuizResult, QuizTextarea, QuizChoice, QuizProgress, QuizTimeOut },
        props: {
            quiz: {
                type: Object
            },
            quizzesResults: {
                type: Array
            }
        },
        data () {
            return {
                questionNumber: 0,
                selectedAnswerIndex: null,
                alphabet: Array.from({ length: 26 }, (_, i) =>
                    String.fromCharCode(65 + i)
                ),
                isCorrectAnswer: null,
                answerSubmitted: null,
                correctTextareaAnswerLength: false,
                textareaAnswer: '',
                results: [],
                attemptStartTime: null,
                startTime: null,
                isQuizPassed: false,
                thirtyMinutesInMs: 30 * 60 * 1000,
                showQuizTimeOut: false
            }
        },
        computed: {
            feedback() {
                if (this.textareaAnswer && this.isCorrectAnswer) {
                    return { text: this.$t('Quiz.answer_submitted'), icon: 'icon-check' }
                }
                return this.isCorrectAnswer
                    ? { text: this.$t('Quiz.answer_correct'), icon: 'icon-check'}
                    : { text: this.$t('Quiz.answer_incorrect', { correctChoice: this.correctChoiceLabel }), icon: 'icon-cross'}
            },
            correctChoiceIndex () {
                return this.quiz.questions[this.questionNumber].choices.findIndex(choice => choice.correct)
            },
            correctChoiceLabel () {
                return this.alphabet[this.correctChoiceIndex]
            },
            progress () {
                return ((this.questionNumber) / (this.quiz.questionsInQuiz)) * 100
            },
            quizType () {
                return this.quiz.questions[this.questionNumber].type
            },
            button () {
                if (this.answerSubmitted && this.questionNumber === this.quiz.questionsInQuiz - 1) {
                    return { label: this.$t('Quiz.finish_test'), icon: 'arrow-right' }
                }
                return this.answerSubmitted ? { label: this.$t('Quiz.next_question'), icon: 'arrow-right' } : { label: this.$t('Quiz.submit_answer'), icon: null }
            },
            disabledButton () {
                switch(this.quizType) {
                    case 'choices':
                        return this.selectedAnswerIndex === null
                    case 'text':
                        return !this.correctTextareaAnswerLength
                    default:
                        return false
                }
            },
            quizStatus() {
                const quiz = this.quizzesResults.find((quiz) => quiz.elementId === this.quiz.elementId)
                return {
                    isCompleted: !!quiz,
                    isPassed: quiz?.passed || false,
                    resultPercentage: quiz?.resultPercentage
                }
            }
        },
        methods: {
            isVimeoUrl(url) {
                const vimeoPlayerRegex = /^https?:\/\/player\.vimeo\.com\/video\/\d+(?:\?[^\s]*)?$/
                return vimeoPlayerRegex.test(url)
            },
            async submitAnswer() {
                if (this.hasThirtyMinutesPassed()) {
                    this.showQuizTimeOut = true
                    return
                }

                if (this.questionNumber < this.quiz.questionsInQuiz) {
                    if (!this.answerSubmitted) {
                        this.validateAnswer()
                    } else if ((this.questionNumber === this.quiz.questionsInQuiz - 1) && this.answerSubmitted) {
                        try {
                            await this.$store.dispatch('Quiz/sendQuizAnswers',
                                {
                                    elementId: this.quiz.elementId,
                                    attemptStartTime: this.attemptStartTime,
                                    attemptEndTime: new Date().toISOString(),
                                    attemptResult: this.getAttemptResult(this.results).status,
                                    attemptPercentage: this.getAttemptResult(this.results).percentage,
                                    answers: this.results.map(({ question, ...rest }) => rest)
                                })
                        } catch (error) {
                            console.error('Error while sending quiz answers:', error);
                        }

                        await this.$store.dispatch('Courses/loadSingleCourse', this.$route.params.courseSlug)

                        this.questionNumber += 1
                        this.selectedAnswerIndex = null
                        this.isCorrectAnswer = null
                        this.clearLocalStorage()

                    } else {
                        this.questionNumber += 1
                        this.selectedAnswerIndex = null
                        this.textareaAnswer = ''
                        this.isCorrectAnswer = null
                        this.answerSubmitted = false
                    }
                }
            },
            getAttemptResult(results) {
                const correctAnswers = results.filter(result => result.isCorrect).length
                const completionPercentage = Math.floor((correctAnswers / this.quiz.questionsInQuiz) * 100)
                this.isQuizPassed = completionPercentage > this.quiz.acceptThreshold

                return {
                    status: this.isQuizPassed ? 'success' : 'fail',
                    percentage: completionPercentage
                }
            },
            validateAnswer () {
                const currentQuestion = this.quiz.questions[this.questionNumber]
                const results = {
                    question: currentQuestion.question,
                    questionId: currentQuestion.id,
                    isCorrect: null,
                    freeText: ''
                }

                if (this.quizType === 'text') {
                    this.isCorrectAnswer = this.correctTextareaAnswerLength
                    results.isCorrect = this.isCorrectAnswer
                    results.freeText = this.textareaAnswer

                } else if (this.quizType === 'choices') {
                    this.isCorrectAnswer = currentQuestion.choices[this.selectedAnswerIndex].correct
                    results.isCorrect = this.isCorrectAnswer
                    results.choiceId = currentQuestion.choices[this.selectedAnswerIndex].id
                }

                this.results.push(results)
                this.saveQuizStateToLocalStorage()
                this.answerSubmitted = true
            },
            updateSelectedChoice(index) {
                this.selectedAnswerIndex = index
            },
            updateTextareaValidation(value) {
                this.correctTextareaAnswerLength = value
            },
            resetQuiz () {
                this.questionNumber = 0
                this.selectedAnswerIndex = null
                this.isCorrectAnswer = null
                this.answerSubmitted = null
                this.results = []
                this.textareaAnswer = ''
                this.correctTextareaAnswerLength = false
                this.showQuizTimeOut = false
                this.quizStatus.isCompleted = false
                this.setQuizStartTime()
                this.clearLocalStorage()
            },
            newQuizReset () {
                this.questionNumber = 0;
                this.results = [];
                this.textareaAnswer = ''
                this.selectedAnswerIndex = null
                this.isCorrectAnswer = null
                this.answerSubmitted = null
                this.correctTextareaAnswerLength = false
                this.showQuizTimeOut = false
            },
            hasThirtyMinutesPassed() {
                const currentTime = new Date().getTime()
                const timePassed = (currentTime - this.startTime) >= this.thirtyMinutesInMs

                if (timePassed) {
                    this.resetQuiz()
                }

                return timePassed
            },
            saveQuizStateToLocalStorage() {
                const savedData = JSON.parse(localStorage.getItem('quizState')) || {};
                savedData[this.quiz.id] = {
                    questionNumber: this.questionNumber,
                    results: this.results
                };
                localStorage.setItem('quizState', JSON.stringify(savedData));
            },
            loadQuizStateFromLocalStorage() {
                const savedData = JSON.parse(localStorage.getItem('quizState')) || {};
                if (savedData[this.quiz?.id]) {
                    this.questionNumber = savedData[this.quiz.id].questionNumber || 0;
                    this.results = savedData[this.quiz.id].results || [];
                }
            },
            clearLocalStorage() {
                const quizStateData = JSON.parse(localStorage.getItem('quizState')) || {};
                delete quizStateData[this.quiz.id];
                localStorage.setItem('quizState', JSON.stringify(quizStateData));
            },
            setQuizStartTime() {
                this.attemptStartTime = new Date().toISOString()
                this.startTime = new Date(this.attemptStartTime).getTime()
            }
        },
        created () {
            this.setQuizStartTime()
        },
        watch: {
            quiz(newValue) {
                if (newValue && newValue.questions && newValue.questions.length) {
                    this.loadQuizStateFromLocalStorage()
                    this.newQuizReset()
                }
                this.isQuizPassed = this.quizStatus.isPassed
            },
            immediate: true,
            questionNumber(newValue) {
                this.saveQuizStateToLocalStorage()
            }
        }
    }
</script>

<style lang="scss" scoped>
    .quiz {
        padding: 30px;
        border-radius: 20px;
        background: $white;

        &__content {
            font-family: PPAgrandir-Medium, sans-serif;
            color: $emperor;
            font-size: 1.6rem;
            display: flex;
            flex-direction: column;
            gap: 30px;
        }

        &__progress {
            display: flex;
            flex-direction: column;
            gap: 14px;
        }

        &__question {
            font-family: PlusJakartaSans-Medium, sans-serif;
            color: $tuatara;
            font-size: 2.4rem;
            line-height: 150%;
        }

        &__hint {
            margin: 0 0 20px;
        }

        &__answers {
            display: flex;
            flex-direction: column;
        }

        &__feedback {
            font-family: PlusJakartaSans-Regular, sans-serif;
            color: $tuatara;
            font-size: 1.6rem;
            display: flex;
            align-items: center;
            border-radius: 5px;
            padding: 20px;
            gap: 10px;

            &.correct {
                border: 1px solid rgba(60, 110, 113, 0.40);
                background: rgba(60, 110, 113, 0.20);

                [class*="icon-"] {
                    color: $william;
                }
            }

            &.incorrect {
                border: 1px solid rgba(183, 69, 69, 0.40);
                background: rgba(183, 69, 69, 0.20);

                [class*="icon-"] {
                    color: $valencia;
                }
            }
        }

        &__submit-button {
            align-self: flex-start;
        }

        &__attachment {
            img {
                height: 172.5px;
                aspect-ratio: 306.67 / 172.50;
                border-radius: 10px;
            }
        }

        &__attachments {
            display: flex;
            flex-wrap: wrap;
            gap: 30px;
        }
    }
</style>
